/**
 * Hedra Avatar Widget Entry Point
 *
 * This file provides the widget initialization API for embedding
 */

import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import { initAnalytics, trackWidgetInitialized } from "./analytics";
import { HedraAvatarWidget } from "./HedraAvatarWidget";
import { widgetStyles } from "./styles";
import type { WidgetInitConfig } from "./types";

// Export React component for Next.js usage
export { HedraAvatarWidget } from "./HedraAvatarWidget";
export type {
  WidgetConfig,
  WidgetEvents,
  WidgetInitConfig,
  WidgetState,
} from "./types";

// Widget instance management
let widgetRoot: Root | null = null;
let widgetContainer: HTMLDivElement | null = null;

/**
 * Detect the origin of the widget script for loading assets (ONNX, VAD models)
 * When embedded cross-origin, we need absolute URLs to load WASM/model files
 */
function detectScriptOrigin(): string {
  try {
    const currentScript = document.currentScript as HTMLScriptElement | null;
    if (currentScript?.src) {
      const scriptUrl = new URL(currentScript.src);
      return scriptUrl.origin;
    }
  } catch {
    // Ignore errors in script origin detection
  }
  return window.location.origin;
}

interface WidgetConfigResponse {
  avatarId: string;
  voiceId: string;
  placeholderImage?: string;
  width?: string;
  height?: string;
  position?: "bottom-right" | "bottom-left";
}

/**
 * Initialize the Hedra Avatar Widget
 *
 * @param config - Widget configuration. Either provide widgetId (to fetch config from server) or avatarId+voiceId (direct config)
 * @returns Promise resolving to cleanup function to destroy the widget
 */
export async function init(config: WidgetInitConfig): Promise<() => void> {
  if (widgetRoot || widgetContainer) {
    console.warn("Widget already initialized. Call destroy() first.");
    return destroy;
  }

  const scriptOrigin = process.env.NEXT_PUBLIC_BASE_URL || detectScriptOrigin();

  let avatarId = config.avatarId;
  let voiceId = config.voiceId;
  let placeholderImage = config.placeholderImage;
  let width = config.width;
  let height = config.height;
  let position = config.position;

  // If widgetId is provided, fetch config from server
  if (config.widgetId && !(avatarId && voiceId)) {
    try {
      const response = await fetch(
        `${scriptOrigin}/api/widget/${config.widgetId}/config`
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to fetch widget config:", errorText);
        return () => {};
      }

      const widgetConfig: WidgetConfigResponse = await response.json();
      avatarId = widgetConfig.avatarId;
      voiceId = widgetConfig.voiceId;
      placeholderImage = widgetConfig.placeholderImage ?? placeholderImage;
      width = widgetConfig.width ?? width;
      height = widgetConfig.height ?? height;
      position = widgetConfig.position ?? position;
    } catch (error) {
      console.error("Failed to fetch widget config:", error);
      return () => {};
    }
  }

  if (!config.widgetId) {
    console.error(
      "HedraAvatarWidget: widgetId is required. Widget not initialized."
    );
    return () => {};
  }

  if (!(avatarId && voiceId)) {
    console.error(
      "HedraAvatarWidget: avatarId and voiceId are required (or provide widgetId). Widget not initialized."
    );
    return () => {};
  }

  const fullConfig = {
    apiBaseUrl: scriptOrigin,
    widgetId: config.widgetId,
    avatarId,
    voiceId,
    placeholderImage: placeholderImage ?? "",
    width: width ?? "400px",
    height: height ?? "600px",
    position: position ?? "bottom-left",
  };

  injectStyles();

  widgetContainer = document.createElement("div");
  widgetContainer.id = "hedra-avatar-widget-root";
  document.body.appendChild(widgetContainer);

  widgetRoot = createRoot(widgetContainer);
  widgetRoot.render(createElement(HedraAvatarWidget, fullConfig));

  // Initialize PostHog analytics (async, non-blocking)
  void initAnalytics(config.widgetId).then(() => {
    trackWidgetInitialized();
  });

  console.log("Hedra Avatar Widget initialized");

  return destroy;
}

/**
 * Destroy the widget and cleanup
 */
export function destroy(): void {
  if (widgetRoot) {
    widgetRoot.unmount();
    widgetRoot = null;
  }

  if (widgetContainer?.parentNode) {
    widgetContainer.parentNode.removeChild(widgetContainer);
    widgetContainer = null;
  }

  const styleElement = document.getElementById("hedra-avatar-widget-styles");
  if (styleElement?.parentNode) {
    styleElement.parentNode.removeChild(styleElement);
  }

  console.log("Hedra Avatar Widget destroyed");
}

/**
 * Inject widget styles into the document
 */
function injectStyles(): void {
  if (document.getElementById("hedra-avatar-widget-styles")) {
    return;
  }

  const styleElement = document.createElement("style");
  styleElement.id = "hedra-avatar-widget-styles";
  styleElement.textContent = widgetStyles;
  document.head.appendChild(styleElement);
}
